import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import styled from "styled-components";

import { ChartTooltip } from "@similarweb/ui-components";

import { swSettings } from "app/common/services/swSettings";
import { toTimeGranularityName, type TimeGranularitySymbol } from "app/common/utils/timeGranularity";
import { toggleItems } from "app/components/widget/widget-utilities/time-granularity";
import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter, minAbbrNumberFilter, percentageSignFilter } from "app/filters/ngFilters";
import { Subtitle } from "app/pages/industry-analysis/category-share/CategoryShareGraph/utils/ChartTooltip";
import { getDomainSubtitle } from "app/pages/industry-analysis/players/market-shares/utils/utils";
import { isLinesOfBusinessSegment } from "app/services/segments/LinesOfBusinessUtils";
import type { ICustomSegment } from "app/services/segments/segmentsApiService";
import type { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";
import { getTooltipHeaderElement } from "app/UtilitiesAndConstants/UtilitiesComponents/chartTooltipHeaderWithMTDSupport";
import { areaChartConfig } from "pro-features/components/Chart/src/configs/areaChart/areaChartConfig";
import { FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import type { EnrichedData, IChart } from "./types";

const getRows = (
    rowData,
    index,
    getUserAndAccountSegmentById: (segmentId: string) => ICustomSegment,
    enrichedData: EnrichedData,
) => {
    const flattenData = [];
    rowData.forEach((singleRowData) => {
        const { id, name } = singleRowData;
        const segment = getUserAndAccountSegmentById(id);
        const enrichedDataItem = enrichedData[id];
        const regionalDomains = enrichedDataItem?.regionalDomains;

        let label;
        let subtitle;

        if (segment) {
            label = segment.domain;
            subtitle = getDomainSubtitle(segment, regionalDomains ? regionalDomains.length : 0);
        } else if (isLinesOfBusinessSegment(id)) {
            // For Lines of Business segments, use domain from enrichedData and DisplayName as subtitle
            label = enrichedDataItem?.domain || name;
            subtitle = enrichedDataItem?.name;
        } else {
            label = name;
            subtitle = getDomainSubtitle(undefined, regionalDomains ? regionalDomains.length : 0);
        }

        flattenData.push({
            domain: {
                label,
                subtitle,
                backgroundColor: singleRowData.color,
            },
            visits: {
                value: singleRowData.data[index]?.y,
            },
        });
    });
    return flattenData;
};

const StyledSubtitle = styled(Subtitle)`
    margin-bottom: 4px;
    padding-left: 4px;
    box-sizing: border-box;
    text-overflow: ellipsis;
    overflow: hidden;
    width: 150px;
    white-space: nowrap;
`;

const getColumnsDefinition = (isPercentage) => {
    return [
        {
            key: "1",
            title: translate("market.players.market.shares.chart.tooltip.domain"),
            selector: "domain" as const,
            align: "left",
            size: ChartTooltip.LabelCellDefaultWidth,
            render: ({ domain }) => {
                const { label, subtitle, backgroundColor } = domain;
                return (
                    <FlexColumn gap={"0 4px"}>
                        <ChartTooltip.LabelCell
                            label={label}
                            backgroundColor={backgroundColor}
                        />
                        {subtitle ? <StyledSubtitle>{subtitle}</StyledSubtitle> : null}
                    </FlexColumn>
                );
            },
        },
        {
            key: "2",
            title: translate("market.players.market.shares.chart.tooltip.traffic.share"),
            selector: "visits" as const,
            align: "right",
            size: ChartTooltip.ValueAndChangeCellDefaultWidth,
            render: ({ visits }) => {
                return (
                    <ChartTooltip.ValueCell
                        value={
                            // @ts-expect-error FIXME: Argument of type 'number' is not assignable to parameter of type 'boolean'
                            isPercentage ? percentageSignFilter()(visits.value, 2) : abbrNumberFilter()(visits.value, 0)
                        }
                    />
                );
            },
        },
    ];
};

export const getChartConfig = (
    chartData: IChart[] = [],
    isPercentage = false,
    getUserAndAccountSegmentById: (segmentId: string) => ICustomSegment,
    enrichedData: EnrichedData,
    timeGranularity: TimeGranularitySymbol,
    toDate: Dayjs,
    isMonthToDate: boolean,
) => {
    const lastSupportedDate =
        isMonthToDate && swSettings.current.lastSupportedDailyDate.isAfter(toDate)
            ? swSettings.current.lastSupportedDailyDate
            : toDate;
    const timeGranularityName = toTimeGranularityName(timeGranularity);
    const timeGranularityChartMetadata = toggleItems[timeGranularityName];
    const xAxisFormatter = ({ value }) => dayjs.utc(value).format(timeGranularityChartMetadata.format);
    const yAxisFormatter = isPercentage
        ? ({ value }) => percentageSignFilter()(value, 2)
        : ({ value }) => minAbbrNumberFilter()(value);

    function tooltipFormatter() {
        const { index } = this.points[0].point;
        const title = getTooltipHeaderElement(timeGranularityName as GranularityNames, this.x, lastSupportedDate, true);
        return (
            <ChartTooltip
                data={getRows(chartData, index, getUserAndAccountSegmentById, enrichedData)}
                columnDefinition={getColumnsDefinition(isPercentage)}
                width={320}
                title={title}
            />
        );
    }

    const args = {
        xAxisFormatter: xAxisFormatter,
        tooltipFormatter: tooltipFormatter,
        height: 350,
        timeGranularity: timeGranularityChartMetadata,
        yAxisFormatter: yAxisFormatter,
        additionalChartConfig: { yAxis: { ceiling: isPercentage ? 1 : null } },
    };

    return areaChartConfig(args);
};
